import React, { useEffect, useState } from 'react';
import {
  listCatalog,
  createCatalog,
  updateCatalog,
  deleteCatalog,
} from '../../api/system/system.api';

// 정가표(billing_catalog) 관리 — 회사 공통 상품·정가(net)·원가(net). sysadmin 전용.
// 설계: design_v5.md §A.1/§D. (A2 Phase A)

const CATEGORY_OPTIONS = ['MONTHLY', 'ACQUISITION', 'MESSAGE', 'MOMENT', 'GIFT', 'ONE_TIME'];

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

const emptyRow = () => ({
  itemType: '',
  category: 'MONTHLY',
  name: '',
  listPriceNet: 0,
  costNet: 0,
  unit: '',
  effectiveFrom: '2026-01',
  effectiveTo: '',
  isActive: true,
  sortOrder: 0,
});

export default function BillingCatalogAdmin() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(emptyRow());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const run = async (fn, okMsg) => {
    setLoading(true);
    setMsg('');
    try {
      await fn();
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setMsg(`오류: ${e?.response?.data?.data || e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const load = () => run(async () => {
    const resp = await listCatalog(false);
    setRows((resp?.data ?? []).map((r) => ({ ...r, effectiveTo: r.effectiveTo || '' })));
  });

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toReq = (r) => ({
    itemType: r.itemType,
    category: r.category,
    name: r.name,
    listPriceNet: Number(r.listPriceNet) || 0,
    costNet: Number(r.costNet) || 0,
    unit: r.unit || null,
    effectiveFrom: r.effectiveFrom,
    effectiveTo: r.effectiveTo || null,
    isActive: !!r.isActive,
    sortOrder: Number(r.sortOrder) || 0,
  });

  const setRowField = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const doSaveRow = (r) => run(async () => {
    await updateCatalog(r.id, toReq(r));
    await load();
  }, `[${r.itemType}] 저장 완료`);

  const doDelete = (r) => run(async () => {
    if (!window.confirm(`[${r.itemType}] 정가행을 삭제할까요?`)) return;
    await deleteCatalog(r.id);
    await load();
  }, '삭제 완료');

  const doCreate = () => run(async () => {
    if (!draft.itemType || !draft.name) { setMsg('itemType/name 은 필수입니다.'); return; }
    await createCatalog(toReq(draft));
    setDraft(emptyRow());
    await load();
  }, '신규 정가행 추가 완료');

  const inputCls = 'border rounded px-1.5 py-1 text-sm w-full';

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-semibold">정가표(billing_catalog)</h2>
        <button className="px-2 py-1 text-sm border rounded" onClick={load} disabled={loading}>새로고침</button>
        {loading && <span className="text-sm text-gray-500">처리 중…</span>}
        {msg && <span className="text-sm text-blue-700">{msg}</span>}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        정가/원가는 <b>VAT 전 공급가(net)</b>. 미확정(TBD) 항목은 정가 0 + 비활성으로 두세요. 사용량형(메시지) 단가는 정가표→코드 폴백으로 정산에 반영됩니다.
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border">
          <thead className="bg-gray-50">
            <tr>
              {['itemType', 'category', 'name', '정가(net)', '원가(net)', 'unit', 'from', 'to', '활성', '순서', ''].map((h) => (
                <th key={h} className="border px-2 py-1 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* 신규 추가 행 */}
            <tr className="bg-yellow-50">
              <td className="border px-1 py-1"><input className={inputCls} value={draft.itemType} onChange={(e) => setDraft({ ...draft, itemType: e.target.value })} placeholder="DANGOLTALK" /></td>
              <td className="border px-1 py-1">
                <select className={inputCls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </td>
              <td className="border px-1 py-1"><input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="단골톡" /></td>
              <td className="border px-1 py-1"><input type="number" className={inputCls} value={draft.listPriceNet} onChange={(e) => setDraft({ ...draft, listPriceNet: e.target.value })} /></td>
              <td className="border px-1 py-1"><input type="number" step="0.01" className={inputCls} value={draft.costNet} onChange={(e) => setDraft({ ...draft, costNet: e.target.value })} /></td>
              <td className="border px-1 py-1"><input className={inputCls} value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="지점당" /></td>
              <td className="border px-1 py-1"><input className={inputCls} value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} placeholder="2026-01" /></td>
              <td className="border px-1 py-1"><input className={inputCls} value={draft.effectiveTo} onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })} placeholder="(없음)" /></td>
              <td className="border px-1 py-1 text-center"><input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} /></td>
              <td className="border px-1 py-1"><input type="number" className={inputCls} value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} /></td>
              <td className="border px-1 py-1"><button className="px-2 py-1 text-sm bg-blue-600 text-white rounded whitespace-nowrap" onClick={doCreate} disabled={loading}>추가</button></td>
            </tr>
            {/* 기존 행 */}
            {rows.map((r, idx) => (
              <tr key={r.id} className={r.isActive ? '' : 'bg-gray-50 text-gray-400'}>
                <td className="border px-1 py-1 whitespace-nowrap">{r.itemType}</td>
                <td className="border px-1 py-1">
                  <select className={inputCls} value={r.category} onChange={(e) => setRowField(idx, 'category', e.target.value)}>
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="border px-1 py-1"><input className={inputCls} value={r.name} onChange={(e) => setRowField(idx, 'name', e.target.value)} /></td>
                <td className="border px-1 py-1"><input type="number" className={inputCls} value={r.listPriceNet} onChange={(e) => setRowField(idx, 'listPriceNet', e.target.value)} /></td>
                <td className="border px-1 py-1"><input type="number" step="0.01" className={inputCls} value={r.costNet} onChange={(e) => setRowField(idx, 'costNet', e.target.value)} /></td>
                <td className="border px-1 py-1"><input className={inputCls} value={r.unit || ''} onChange={(e) => setRowField(idx, 'unit', e.target.value)} /></td>
                <td className="border px-1 py-1"><input className={inputCls} value={r.effectiveFrom} onChange={(e) => setRowField(idx, 'effectiveFrom', e.target.value)} /></td>
                <td className="border px-1 py-1"><input className={inputCls} value={r.effectiveTo} onChange={(e) => setRowField(idx, 'effectiveTo', e.target.value)} /></td>
                <td className="border px-1 py-1 text-center"><input type="checkbox" checked={!!r.isActive} onChange={(e) => setRowField(idx, 'isActive', e.target.checked)} /></td>
                <td className="border px-1 py-1"><input type="number" className={inputCls} value={r.sortOrder} onChange={(e) => setRowField(idx, 'sortOrder', e.target.value)} /></td>
                <td className="border px-1 py-1 whitespace-nowrap">
                  <button className="px-2 py-1 text-xs border rounded mr-1" onClick={() => doSaveRow(r)} disabled={loading}>저장</button>
                  <button className="px-2 py-1 text-xs border rounded text-red-600" onClick={() => doDelete(r)} disabled={loading}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500 mt-2">총 {rows.length}개 · 정가 합(월정액 기준 표시용): {won(rows.filter((r) => r.category === 'MONTHLY').reduce((s, r) => s + (Number(r.listPriceNet) || 0), 0))}</div>
    </div>
  );
}
